import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Floating WhatsApp button — bottom-right, opens a wa.me chat.
// Hidden on admin routes and on <sm viewports it slides into a compact FAB.
const WHATSAPP_NUMBER = '6598883331'; // wa.me expects the number without '+' or spaces
const WA_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hi Hei Web Creations, I'd like to enquire about your services."
)}`;

const WhatsAppButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Hide on admin pages
    const check = () => setVisible(!window.location.pathname.startsWith('/admin'));
    check();
    window.addEventListener('popstate', check);
    // Also patch pushState/replaceState so client-side nav updates visibility
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      origPush.apply(this, args);
      check();
    };
    window.history.replaceState = function (...args) {
      origReplace.apply(this, args);
      check();
    };
    return () => {
      window.removeEventListener('popstate', check);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.a
          key="whatsapp-fab"
          href={WA_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-fab"
          aria-label="Chat with us on WhatsApp"
          initial={{ opacity: 0, y: 40, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.8 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.95 }}
          className="fixed z-[60] bottom-6 right-6 md:bottom-8 md:right-8 flex items-center gap-3 bg-[#25D366] text-white rounded-full pl-4 pr-5 py-3.5 shadow-2xl shadow-[#25D366]/40 hover:shadow-[#25D366]/60 transition-shadow group"
        >
          {/* Pulse ring */}
          <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 animate-ping" />
          {/* WhatsApp glyph */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
            className="relative w-6 h-6 fill-white flex-shrink-0"
            aria-hidden="true"
          >
            <path d="M19.11 17.61c-.28-.14-1.63-.8-1.88-.9-.25-.09-.44-.14-.62.14-.19.28-.72.9-.88 1.08-.16.19-.32.21-.6.07-.28-.14-1.17-.43-2.23-1.37a8.3 8.3 0 0 1-1.54-1.91c-.16-.28 0-.43.12-.57.13-.13.28-.32.42-.49.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.49-.07-.14-.62-1.5-.85-2.05-.23-.55-.46-.47-.62-.48h-.53c-.19 0-.5.07-.76.35-.26.28-1 .98-1 2.39s1.03 2.77 1.17 2.96c.14.19 2.03 3.09 4.92 4.33.69.3 1.22.48 1.64.61.69.22 1.31.19 1.81.11.55-.08 1.63-.66 1.86-1.3.23-.64.23-1.19.16-1.3-.07-.11-.26-.19-.54-.33zm-5.14 7.0h-.01c-1.69 0-3.34-.45-4.78-1.31l-.34-.2-3.55.93.95-3.46-.22-.35a9.86 9.86 0 0 1-1.5-5.26c0-5.44 4.43-9.87 9.88-9.87 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 0 1 2.89 6.99c0 5.44-4.44 9.87-9.87 9.87h-.44zm8.4-18.31A11.79 11.79 0 0 0 13.97 3C7.41 3 2.07 8.34 2.07 14.9c0 2.1.55 4.14 1.59 5.94L2 27l6.34-1.66a11.9 11.9 0 0 0 5.62 1.43h.01c6.55 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.49-8.42z" />
          </svg>
          {/* Label — hidden on very small screens */}
          <span className="relative font-medium text-sm hidden sm:inline whitespace-nowrap">
            Chat on WhatsApp
          </span>
        </motion.a>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppButton;
